/*
 * DiveCatalog — Delivery Promise Engine, one-line promises for cart items
 * AND collection product cards.
 *
 * Placeholders (emitted by cart-items.liquid and price.liquid card contexts):
 *   <div data-dcdp-cart-line data-stamp="…" data-cart-total="123.45"></div>
 *   <div data-dcdp-card-line data-stamp="…" data-price="49.95"></div>
 *
 * Collection cards AND cart/drawer lines render a mini version of the PDP
 * block — icon rows with "Pick up in store <b>Tomorrow, Jul 31</b>" and
 * "Get it by <b>Saturday, Aug 1</b> • FREE". Made-to-order keeps its plain
 * italic range line.
 *
 * Results are cached in sessionStorage (30 min) so collection browsing costs
 * almost nothing. Same rules as delivery-promise.js: fail soft, DOM built node
 * by node, only the ZIP ever leaves the page.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) return;
  if (window.__dcdpCartInit) return;
  window.__dcdpCartInit = true;

  var ENDPOINT = 'https://ycobmooadlvzkbqawzye.supabase.co/functions/v1/delivery-promise';
  var GEO_ENDPOINT = 'https://ipapi.co/json/';
  var LS_ZIP_KEY = 'dc_promise_zip';
  var SS_GUESS_KEY = 'dc_promise_zip_guess';
  var SS_CACHE_PREFIX = 'dcdp_r1|';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var ZIP_RE = /^\d{5}(-\d{4})?$/;
  var API_TIMEOUT_MS = 3000;
  var GEO_TIMEOUT_MS = 1500;

  function debug() {
    try {
      if (window.console && console.debug) console.debug.apply(console, arguments);
    } catch (e) { /* noop */ }
  }

  function storageGet(storage, key) {
    try { return window[storage].getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* noop */ }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* noop */ }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function isZip(value) {
    return typeof value === 'string' && ZIP_RE.test(value.trim());
  }

  var DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** "2026-08-06" -> "Thu, Aug 6" (UTC-noon parse avoids timezone day-shift). */
  function formatShortDate(iso) {
    if (typeof iso !== 'string' || iso.length < 10) return '';
    var d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
    if (isNaN(d.getTime())) return '';
    return DOW_SHORT[d.getUTCDay()] + ', ' + MON_SHORT[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  /**
   * Date label, same rules as the PDP block: always a day word PLUS the
   * date — "Today, Jul 30" / "Tomorrow, Jul 31" / "Saturday, Aug 1" /
   * "Friday, Aug 7" — from the shopper's local calendar.
   */
  function dayLabel(y, mIdx, d) {
    var now = new Date();
    var diff = Math.round((Date.UTC(y, mIdx, d) -
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 864e5);
    var dateTxt = MON_SHORT[mIdx] + ' ' + d;
    if (diff === 0) return 'Today, ' + dateTxt;
    if (diff === 1) return 'Tomorrow, ' + dateTxt;
    return DOW_FULL[new Date(Date.UTC(y, mIdx, d)).getUTCDay()] + ', ' + dateTxt;
  }

  function relLabel(iso) {
    if (typeof iso !== 'string' || iso.length < 10) return '';
    var y = Number(iso.slice(0, 4)), m = Number(iso.slice(5, 7)), d = Number(iso.slice(8, 10));
    if (!y || !m || !d) return '';
    return dayLabel(y, m - 1, d);
  }

  /** "on Friday" -> "Tomorrow, Jul 31"; "today by 5:30pm" passes through. */
  function pickupLabel(text) {
    if (typeof text !== 'string' || !text) return '';
    if (text.indexOf('on ') !== 0) return text;
    var name = text.slice(3);
    var idx = DOW_FULL.indexOf(name);
    if (idx < 0) return name;
    var now = new Date();
    for (var i = 1; i <= 7; i++) {
      var cand = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      if (cand.getDay() === idx) {
        return dayLabel(cand.getFullYear(), cand.getMonth(), cand.getDate());
      }
    }
    return name;
  }

  /* Same outline icons as the PDP block, sized for cards. Constant markup
     only — never interpolated with API or user data. */
  var CARD_ICONS = {
    store: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 9.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5"/><path d="M4.6 3h14.8L21 7.2a2.3 2.3 0 0 1-4.5.6A2.3 2.3 0 0 1 12 7.6 2.3 2.3 0 0 1 7.5 7.8 2.3 2.3 0 0 1 3 7.2L4.6 3z"/><path d="M9.5 20v-5.5h5V20"/></svg>',
    box: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M21 7.5 12 3 3 7.5l9 4.5 9-4.5z"/><path d="M3 7.5v9L12 21l9-4.5v-9"/><path d="M12 12v9"/><path d="M7.5 5.25l9 4.5"/></svg>'
  };

  function cardIcon(name) {
    var holder = document.createElement('span');
    holder.className = 'dcdp-cicon';
    holder.innerHTML = CARD_ICONS[name] || '';
    return holder;
  }

  function fetchJSON(url, options, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    var opts = options || {};
    if (controller) {
      opts = Object.assign({}, opts, { signal: controller.signal });
      timer = window.setTimeout(function () {
        try { controller.abort(); } catch (e) { /* noop */ }
      }, timeoutMs);
    }
    return fetch(url, opts).then(function (res) {
      if (!res.ok) throw new Error('http_' + res.status);
      return res.json();
    }).then(function (json) {
      if (timer) window.clearTimeout(timer);
      return json;
    }, function (err) {
      if (timer) window.clearTimeout(timer);
      throw err;
    });
  }

  /* Same acquisition order as the PDP block, minus the manual prompt — lines
     stay quiet when no ZIP is known rather than nagging per item. */
  var zipPromise = null;
  function resolveZip() {
    if (zipPromise) return zipPromise;
    zipPromise = Promise.resolve().then(function () {
      var saved = (storageGet('localStorage', LS_ZIP_KEY) || '').trim();
      if (isZip(saved)) return saved;
      var guessed = (storageGet('sessionStorage', SS_GUESS_KEY) || '').trim();
      if (isZip(guessed)) return guessed;
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.analyticsProcessingAllowed !== 'function' ||
            cp.analyticsProcessingAllowed() !== true) {
          return null; // no consent signal -> no geo-IP guess
        }
      } catch (e) {
        return null;
      }
      return fetchJSON(GEO_ENDPOINT, { method: 'GET', mode: 'cors' }, GEO_TIMEOUT_MS)
        .then(function (data) {
          var postal = data && (data.postal || data.zip || data.postal_code);
          postal = typeof postal === 'string' ? postal.trim() : '';
          if (!isZip(postal)) return null;
          ssSet(SS_GUESS_KEY, postal);
          return postal;
        })
        .catch(function () { return null; });
    }).catch(function () { return null; });
    return zipPromise;
  }

  /* The engine only decides on price BRACKETS, so exact prices are
     over-specific cache keys: 24 cards -> 24 API calls. Snapping the value
     DOWN to the union of every real threshold edge in the rate tables gives
     an IDENTICAL promise while collapsing a collection page to ~2-4 calls.
     (500.01 exists because the 68D direct-vs-JIT rule is strictly >$500.) */
  var PRICE_EDGES = [1000, 900, 800, 600, 500.01, 500, 300, 250, 200, 150, 100, 50, 30, 25, 20, 15, 10, 0];
  function quantize(value) {
    if (typeof value !== 'number' || !isFinite(value) || value < 0) return value;
    for (var i = 0; i < PRICE_EDGES.length; i++) {
      if (value >= PRICE_EDGES[i]) return PRICE_EDGES[i];
    }
    return 0;
  }

  /* One lookup per DISTINCT stamp+price-bracket, memoised in-page AND in
     sessionStorage so collection pages barely touch the network. */
  var resultCache = {};
  function promiseFor(zip, stampRaw, cartValue) {
    cartValue = quantize(cartValue);
    var key = zip + '|' + stampRaw + '|' + cartValue;
    if (resultCache[key]) return resultCache[key];

    var cached = storageGet('sessionStorage', SS_CACHE_PREFIX + key);
    if (cached) {
      try {
        var entry = JSON.parse(cached);
        if (entry && (Date.now() - entry.t) < CACHE_TTL_MS) {
          return (resultCache[key] = Promise.resolve(entry.r));
        }
      } catch (e) { /* fall through to fetch */ }
    }

    var stamp;
    try {
      stamp = JSON.parse(stampRaw);
    } catch (e) {
      return (resultCache[key] = Promise.resolve(null));
    }
    if (!stamp || typeof stamp !== 'object' || stamp.location_id == null) {
      return (resultCache[key] = Promise.resolve(null));
    }
    resultCache[key] = fetchJSON(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ zip: zip, stamp: stamp, cart_value: cartValue })
    }, API_TIMEOUT_MS).then(function (data) {
      var result = (data && data.ok === true) ? data.result : null;
      if (!result || !result.tier || result.tier === 'none') result = null;
      ssSet(SS_CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), r: result }));
      return result;
    }).catch(function (err) {
      debug('[dcdp-lines] lookup failed', err && err.message);
      return null;
    });
    return resultCache[key];
  }

  /** The bold-date shipping sentence shared by cards and cart lines. */
  function shipSentence(container, result, rel, overnight) {
    var prefix = document.createElement('span');
    prefix.className = 'dcdp-headline-prefix';
    prefix.textContent = (overnight ? '⚡ ' : '') +
      (result.tier === 'firm' ? 'Get it by ' : 'Estimated delivery by ');
    var date = document.createElement('strong');
    date.className = 'dcdp-headline-date';
    date.textContent = rel;
    container.appendChild(prefix);
    container.appendChild(date);
    if (result.tier === 'firm' && result.service && result.service.free) {
      container.appendChild(document.createTextNode(' • '));
      var free = document.createElement('strong');
      free.className = 'dcdp-free';
      free.textContent = 'FREE';
      container.appendChild(free);
    }
  }

  /* Cards, cart page and drawer all get the mini PDP block (icon rows
     incl. pickup); made-to-order/dateless results keep the compact line. */
  function renderLine(node, result) {
    while (node.firstChild) node.removeChild(node.firstChild);
    if (!result) return;
    var display = result.display || {};
    var headline = typeof display.headline === 'string' ? display.headline : '';

    // The core only emits the Overnight headline for true next-day arrival.
    var overnight = result.tier === 'firm' && headline.indexOf('Overnight') !== -1;
    var usedBlock = result.tier !== 'range' && !!(result.deliver_by);
    var rel = result.deliver_by ? relLabel(result.deliver_by) : '';

    if (usedBlock && rel) {
      var wrap = document.createElement('span');
      wrap.className = 'dcdp-card-promise';

      if (result.tier === 'firm' && result.pickup && result.pickup.available &&
          typeof result.pickup.text === 'string' && result.pickup.text) {
        var prow = document.createElement('span');
        prow.className = 'dcdp-crow dcdp-crow--pickup';
        prow.appendChild(cardIcon('store'));
        var pbody = document.createElement('span');
        pbody.appendChild(document.createTextNode('Pick up in store '));
        var pstrong = document.createElement('strong');
        pstrong.textContent = pickupLabel(result.pickup.text);
        pbody.appendChild(pstrong);
        prow.appendChild(pbody);
        wrap.appendChild(prow);
      }

      var srow = document.createElement('span');
      srow.className = 'dcdp-crow dcdp-crow--ship' +
        (overnight ? ' dcdp-crow--overnight' : '') +
        (result.tier === 'estimated' ? ' dcdp-crow--estimated' : '');
      srow.appendChild(cardIcon('box'));
      var sbody = document.createElement('span');
      shipSentence(sbody, result, rel, overnight);
      srow.appendChild(sbody);
      wrap.appendChild(srow);
      node.appendChild(wrap);
    } else {
      var line = document.createElement('span');
      line.className = 'dcdp-cart-promise dcdp-cart-promise--' + result.tier +
        (overnight ? ' dcdp-cart-promise--overnight' : '');
      if (result.tier === 'range' || !rel) {
        // Made-to-order (and anything dateless) keeps the config headline.
        if (!headline) return;
        line.textContent = headline;
      } else {
        shipSentence(line, result, rel, overnight);
      }
      node.appendChild(line);
    }

    // Compact paid-upgrade hint: "⚡ As soon as Tomorrow, Jul 31".
    if (result.fastest && result.fastest.deliver_by) {
      var fastRel = relLabel(result.fastest.deliver_by) ||
        formatShortDate(result.fastest.deliver_by);
      if (fastRel) {
        var fastLine = document.createElement('span');
        fastLine.className = 'dcdp-cart-fastest' + (usedBlock && rel ? ' dcdp-cart-fastest--card' : '');
        fastLine.textContent = '⚡ As soon as ' + fastRel;
        node.appendChild(fastLine);
      }
    }
    node.setAttribute('data-dcdp-done', '1');
  }

  /* All of a page's distinct lookups travel in ONE batch request — the
     per-call ~1s round trip was the whole reason collections felt slow. */
  function batchFetch(zip, misses) {
    if (!misses.length) return Promise.resolve();
    var entries = misses.slice(0, 30);
    return fetchJSON(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        zip: zip,
        batch: entries.map(function (m) { return { stamp: m.stamp, cart_value: m.cartValue }; }),
      })
    }, 8000).then(function (data) {
      var results = (data && data.ok === true && data.results) || [];
      for (var i = 0; i < entries.length; i++) {
        var result = results[i] || null;
        if (result && (!result.tier || result.tier === 'none')) result = null;
        resultCache[entries[i].key] = Promise.resolve(result);
        ssSet(SS_CACHE_PREFIX + entries[i].key, JSON.stringify({ t: Date.now(), r: result }));
      }
      if (misses.length > 30) return batchFetch(zip, misses.slice(30));
    }).catch(function (err) {
      debug('[dcdp-lines] batch failed', err && err.message);
      entries.forEach(function (m) { resultCache[m.key] = Promise.resolve(null); });
    });
  }

  /* ---- ZIP bar: collection / home / search / cart -----------------------
     The PDP has always had its own ZIP prompt, but card lines only ever
     resolved a ZIP silently (saved value -> geo-IP guess, and the guess
     needs analytics consent). A first-time visitor who declines cookies
     therefore saw NO dates anywhere outside a product page (Jason,
     2026-08-04). This mounts ONE bar above the product grid — a prompt when
     no ZIP is known, "Delivery dates for 92614 - Change" once it is — and
     writes the same localStorage key the PDP reads, so the shopper enters it
     once for the whole site. */
  var ZIPBAR_FLAG = '__dcdpZipBar';
  var zipBarState = null;   // 'prompt' | '<zip>' — guards against re-render while typing
  var currentZip = null;

  /** Smallest ancestor holding 2+ placeholders = the grid; else the card itself. */
  function gridAnchor() {
    var first = document.querySelector('[data-dcdp-card-line]') ||
                document.querySelector('[data-dcdp-cart-line]');
    if (!first) return null;
    var node = first;
    for (var hops = 0; hops < 8; hops++) {
      node = node.parentNode;
      if (!node || node === document.body || !node.parentNode) break;
      if (node.querySelectorAll &&
          node.querySelectorAll('[data-dcdp-card-line],[data-dcdp-cart-line]').length >= 2) {
        return node;
      }
    }
    return (first.parentNode && first.parentNode.parentNode) ? first.parentNode : null;
  }

  /** Re-run every line against a newly supplied ZIP. */
  function applyNewZip(value) {
    currentZip = value;
    zipPromise = Promise.resolve(value);        // replace the memoised resolution
    var stale = document.querySelectorAll('[data-dcdp-card-line],[data-dcdp-cart-line]');
    for (var i = 0; i < stale.length; i++) {
      stale[i].removeAttribute('data-dcdp-done');
      while (stale[i].firstChild) stale[i].removeChild(stale[i].firstChild);
    }
    paintZipBar(value);
    renderAll();
  }

  function paintZipBar(zip) {
    var bar = document.getElementById('dcdp-zipbar');
    if (!bar) return;
    while (bar.firstChild) bar.removeChild(bar.firstChild);
    bar.appendChild(el('span', 'dcdp-zipbar-pin', '📍'));

    if (isZip(zip)) {
      zipBarState = zip;
      var line = el('span', 'dcdp-zipbar-text');
      line.appendChild(document.createTextNode('Delivery dates for '));
      line.appendChild(el('strong', null, zip));
      bar.appendChild(line);
      var change = el('button', 'dcdp-link', 'Change');
      change.type = 'button';
      change.addEventListener('click', function () { paintZipBar(null); });
      bar.appendChild(change);
      return;
    }

    zipBarState = 'prompt';
    bar.appendChild(el('span', 'dcdp-zipbar-text', 'Enter your ZIP code to see delivery dates'));
    var input = el('input', 'dcdp-input');
    input.type = 'text';
    input.autocomplete = 'postal-code';
    input.inputMode = 'numeric';
    input.maxLength = 10;
    input.placeholder = '90210';
    input.setAttribute('aria-label', 'ZIP code');
    var go = el('button', 'dcdp-button', 'Show dates');
    go.type = 'button';
    var error = el('span', 'dcdp-error');
    error.setAttribute('role', 'alert');

    var apply = function () {
      var value = (input.value || '').trim();
      if (!isZip(value)) {                       // validated before anything is sent
        error.textContent = 'Enter a 5-digit ZIP code.';
        input.setAttribute('aria-invalid', 'true');
        return;
      }
      error.textContent = '';
      input.removeAttribute('aria-invalid');
      lsSet(LS_ZIP_KEY, value);
      try {
        window.dispatchEvent(new CustomEvent('dcdp:zipchange', { detail: { zip: value } }));
      } catch (e) { /* noop */ }
      applyNewZip(value);
    };
    go.addEventListener('click', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); apply(); }
    });

    bar.appendChild(input);
    bar.appendChild(go);
    bar.appendChild(error);
    var saved = (storageGet('localStorage', LS_ZIP_KEY) || '').trim();
    if (isZip(saved)) input.value = saved;      // "Change" pre-fills the old value
    if (window[ZIPBAR_FLAG] === 'user') { try { input.focus(); } catch (e) { /* noop */ } }
  }

  function ensureZipBar(zip) {
    try {
      var want = isZip(zip) ? zip : 'prompt';
      if (window[ZIPBAR_FLAG] && zipBarState === want) return;  // never clobber typing
      if (!window[ZIPBAR_FLAG]) {
        var anchor = gridAnchor();
        if (!anchor || !anchor.parentNode) return;
        var bar = el('div', 'dcdp-zipbar');
        bar.id = 'dcdp-zipbar';
        anchor.parentNode.insertBefore(bar, anchor);
        window[ZIPBAR_FLAG] = true;
      }
      currentZip = isZip(zip) ? zip : null;
      paintZipBar(zip);
    } catch (e) { debug('[dcdp-lines] zip bar', e); }
  }

  /* The PDP writes the same key; mirror its change instantly (cart drawer is
     on the PDP too) and pick up other tabs via the storage event. */
  try {
    window.addEventListener('dcdp:zipchange', function (e) {
      var next = e && e.detail && e.detail.zip;
      if (isZip(next) && next !== currentZip) applyNewZip(next);
    });
    window.addEventListener('storage', function (e) {
      if (e && e.key === LS_ZIP_KEY && isZip(e.newValue) && e.newValue !== currentZip) {
        applyNewZip(e.newValue);
      }
    });
  } catch (e) { /* noop */ }

  var rendering = false;
  function renderAll() {
    if (rendering) return;
    var nodes = document.querySelectorAll(
      '[data-dcdp-cart-line]:not([data-dcdp-done]), [data-dcdp-card-line]:not([data-dcdp-done])'
    );
    if (!nodes.length) return;
    rendering = true;
    resolveZip().then(function (zip) {
      ensureZipBar(zip);   // prompt when unknown, "Delivery dates for X" once known
      if (!isZip(zip)) { rendering = false; return; }
      var jobs = [];
      var misses = {};
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        var stampRaw = node.getAttribute('data-stamp') || '';
        if (!stampRaw) { node.setAttribute('data-dcdp-done', '1'); continue; }
        // Cart lines price by the CART TOTAL (the whole order ships
        // together); card lines price by the product's own price.
        var raw = node.hasAttribute('data-cart-total')
          ? node.getAttribute('data-cart-total')
          : node.getAttribute('data-price');
        var value = parseFloat(raw || '');
        var cartValue = quantize(isFinite(value) && value >= 0 ? value : null);
        var key = zip + '|' + stampRaw + '|' + cartValue;
        jobs.push({ node: node, key: key });
        if (!resultCache[key]) {
          var cached = storageGet('sessionStorage', SS_CACHE_PREFIX + key);
          var hit = false;
          if (cached) {
            try {
              var entry = JSON.parse(cached);
              if (entry && (Date.now() - entry.t) < CACHE_TTL_MS) {
                resultCache[key] = Promise.resolve(entry.r);
                hit = true;
              }
            } catch (e) { /* miss */ }
          }
          if (!hit && !misses[key]) {
            var stamp = null;
            try { stamp = JSON.parse(stampRaw); } catch (e) { /* bad stamp */ }
            if (stamp && typeof stamp === 'object' && stamp.location_id != null) {
              misses[key] = { key: key, stamp: stamp, cartValue: cartValue };
            } else {
              resultCache[key] = Promise.resolve(null);
            }
          }
        }
      }
      var missList = Object.keys(misses).map(function (k) { return misses[k]; });
      batchFetch(zip, missList).then(function () {
        jobs.forEach(function (job) {
          (resultCache[job.key] || Promise.resolve(null)).then(function (result) {
            try {
              renderLine(job.node, result);
            } catch (e) { debug('[dcdp-lines] render', e); }
            // processed either way — a null result must not re-queue forever
            job.node.setAttribute('data-dcdp-done', '1');
          });
        });
        rendering = false;
      }, function () { rendering = false; });
    }, function () { rendering = false; });
  }

  /* Cart updates, drawer re-renders, infinite-scroll collection grids — all
     inject fresh placeholders (scripts inside injected HTML never re-run). */
  var debounceTimer = null;
  function scheduleRender() {
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(renderAll, 200);
  }

  function init() {
    renderAll();
    try {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scheduleRender();
            return;
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      debug('[dcdp-lines] observer unavailable', e);
    }
    // The theme fires cart:refresh after drawer/cart updates — belt & braces.
    document.documentElement.addEventListener('cart:refresh', scheduleRender);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
